import { useCallback, useRef, useState } from 'react';

import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import { Button, Input, InputMask } from '~/components';
import InputDate from '~/components/InputDate';
import { useAuth } from '~/hooks/auth';
import getValidationErrors from '~/utils/getValidationErrors';
import {
  regex_cpf,
  regex_cep,
  regex_remove_all_non_digits,
} from '~/utils/regex';

import { Container, Wrapper, Title } from './styles';

export interface SignUpCompleteFormData {
  name?: string;
  cpf?: string;
  birth_date: string;
  address_country: string;
  address_state: string;
  address_city: string;
  address_street: string;
  address_neighborhood: string;
  address_zip_code: string;
  address_number: string;
  address_complement: string;
  cref: string;
  holder_name: string;
  card_expiration: string;
  card_number: string;
  card_cvv: string;
  registry_code: string;
}

const SignUpComplete = () => {
  const { push } = useHistory();
  const { userData } = useAuth();
  const { t } = useTranslation(['Yup', 'Toast', 'Professional:SignUpComplete']);

  const formRef = useRef<FormHandles>(null);

  const [loading, setLoading] = useState(false);

  const getAddressByZipcode = useCallback(
    async (text) => {
      if (text.length === 8) {
        const response = await axios.get(
          `https://viacep.com.br/ws/${text}/json/`,
        );

        if (response.data.logradouro) {
          const currentData = formRef.current?.getData();

          formRef.current?.setData({
            ...currentData,
            address_street: response.data.logradouro,
            address_neighborhood: response.data.bairro,
            address_city: response.data.localidade,
            address_state: response.data.uf,
            address_country: 'Brasil',
          });
        } else {
          toast.error(t('Toast:cep_not_found'));
        }
      }
    },
    [t],
  );

  const handleSubmit = useCallback(
    async (data: SignUpCompleteFormData) => {
      try {
        setLoading(true);

        const schema = Yup.object().shape({
          name: Yup.string().required(t('Yup:name_required')),
          cpf: Yup.string()
            .required(t('Yup:cpf_required'))
            .matches(regex_cpf, {
              excludeEmptyString: true,
              message: t('Yup:cpf_incomplete'),
            }),
          cref: Yup.string().required(t('Yup:professional_record_required')),
          birth_date: Yup.string().required(t('Yup:birth_date_required')),
          address_zip_code: Yup.string()
            .matches(regex_cep, {
              excludeEmptyString: true,
              message: t('Yup:address_zip_code_incomplete'),
            })
            .required(t('Yup:address_zip_code_required')),
          address_street: Yup.string().required(
            t('Yup:address_street_required'),
          ),
          address_number: Yup.string().required(
            t('Yup:address_number_required'),
          ),
          address_complement: Yup.string(),
          address_city: Yup.string().required(t('Yup:address_city_required')),
          address_state: Yup.string().required(t('Yup:address_state_required')),
          address_country: Yup.string().required(
            t('Yup:address_country_required'),
          ),
        });

        await schema.validate(data, {
          abortEarly: false,
        });

        const { cpf, address_zip_code } = data;
        const cpf_only_numbers = cpf?.replace(regex_remove_all_non_digits, '');
        const address_zip_code_only_numbers = address_zip_code.replace(
          regex_remove_all_non_digits,
          '',
        );

        const formatedData = {
          ...data,
          cpf: cpf_only_numbers,
          address_zip_code: address_zip_code_only_numbers,
        };

        push('/sign-plan', {
          userData,
          completeSignUpData: formatedData,
        });
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);

          formRef.current?.setErrors(errors);
        }

        toast.error(t('Toast:error'));
      } finally {
        setLoading(false);
      }
    },
    [push, t, userData],
  );

  // eslint-disable-next-line no-return-assign
  return (
    <Container>
      <Wrapper>
        <Title>{t('Professional:SignUpComplete:title')}</Title>

        <>
          <Form
            // initialData={{
            //   name: userData.user.name,
            //   cpf: userData.user.professional.cpf,
            // }}
            ref={formRef}
            onSubmit={handleSubmit}
          >
            <Input
              name="name"
              placeholder={t('Professional:SignUpComplete:name_placeholder')}
              style={{ fontSize: 16 }}
              containerStyle={{ height: 45 }}
            />

            <InputMask
              name="cpf"
              placeholder={t('Professional:SignUpComplete:cpf_placeholder')}
              style={{ fontSize: 16 }}
              containerStyle={{ height: 45 }}
              mask="999.999.999-99"
            />

            <Input
              name="cref"
              placeholder={t(
                'Professional:SignUpComplete:professional_record_placeholder',
              )}
              style={{ fontSize: 16 }}
              containerStyle={{ height: 45 }}
            />

            <InputDate
              name="birth_date"
              placeholder={t(
                'Professional:SignUpComplete:birth_date_placeholder',
              )}
              // placeholder="asdfasdf"
              style={{ fontSize: 16 }}
            />

            <InputMask
              name="address_zip_code"
              placeholder={t(
                'Professional:SignUpComplete:address_zip_code_placeholder',
              )}
              style={{ fontSize: 16 }}
              containerStyle={{ height: 45 }}
              mask="99999-999"
              onChange={(text) => {
                getAddressByZipcode(
                  text.target.value.replace(regex_remove_all_non_digits, ''),
                );
              }}
            />

            <Input
              name="address_street"
              placeholder={t(
                'Professional:SignUpComplete:address_street_placeholder',
              )}
              style={{ fontSize: 16 }}
              containerStyle={{ height: 45 }}
            />

            <Input
              name="address_number"
              placeholder={t(
                'Professional:SignUpComplete:address_number_placeholder',
              )}
              style={{ fontSize: 16 }}
              containerStyle={{ height: 45 }}
            />

            <Input
              name="address_complement"
              placeholder={t(
                'Professional:SignUpComplete:address_complement_placeholder',
              )}
              style={{ fontSize: 16 }}
              containerStyle={{ height: 45 }}
            />

            <Input
              name="address_neighborhood"
              placeholder="Bairro"
              style={{ fontSize: 16 }}
              containerStyle={{ height: 45 }}
            />

            <Input
              name="address_city"
              placeholder={t(
                'Professional:SignUpComplete:address_city_placeholder',
              )}
              style={{ fontSize: 16 }}
              containerStyle={{ height: 45 }}
            />

            <Input
              name="address_state"
              placeholder={t(
                'Professional:SignUpComplete:address_state_placeholder',
              )}
              style={{ fontSize: 16 }}
              containerStyle={{ height: 45 }}
            />

            <Input
              name="address_country"
              placeholder={t(
                'Professional:SignUpComplete:address_country_placeholder',
              )}
              style={{ fontSize: 16 }}
              containerStyle={{ height: 45 }}
            />
          </Form>
        </>

        <Button
          onClick={() => formRef.current?.submitForm()}
          style={{ maxWidth: 387 }}
          loading={loading}
        >
          {t('Professional:SignUpComplete:button_continue')}
        </Button>
      </Wrapper>
    </Container>
  );
};

export default SignUpComplete;
